import type { Device, Merchant, MerchantStore, Receipt, UserInToken } from '../../../common/types';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteItem from '../components/DeleteReceipt';
import ListReceipts from '../components/ListReceipt';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Pagination from '../../../components/Pagination';
import ReceiptInfo from '../components/ReceiptInfo';
import SuccessMessage from '../../../components/success-message';
import { useGetAllReceiptsQuery } from '../receipt.api-slice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  filter: 'all' | 'merchant' | 'store' | 'device';
  merchant?: Merchant;
  merchantStore?: MerchantStore;
  device?: Device;
  authUser?: UserInToken | null;
}

const getSampleReceiptHeaderAndLink = ({ filter, merchant, merchantStore, device }: IProps): string => {
  const commonHeader = 'List of Receipts';
  const header =
    filter === 'device' && device != null
      ? `${commonHeader} of ${device.name}`
      : filter === 'store' && merchantStore != null
      ? `${commonHeader} of ${merchantStore.name}`
      : filter === 'merchant' && merchant != null
      ? `${commonHeader} of ${merchant.name}`
      : commonHeader;

  return header;
};

const ListReceiptsPage: React.FC<IProps> = ({ filter = 'all', merchant, merchantStore, device }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const { data: records, isLoading } = useGetAllReceiptsQuery({ page });
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [deletingItem, setDeletingItem] = useState<Receipt | null>(null);
  const [selectedItem, setSelectedItem] = useState<Receipt | null>(null);

  const toggleDeleteModal = (): void => {
    if (openDeleteModal) {
      setDeletingItem(null);
    }
    setOpenDeleteModal(!openDeleteModal);
  };

  const toggleInfoModal = (): void => {
    if (openInfoModal) {
      setSelectedItem(null);
    }
    setOpenInfoModal(!openInfoModal);
  };

  const handleSelectItem = (item: Receipt | null): void => {
    setSelectedItem(item);
  };

  const handleDeletingItem = (item: Receipt | null): void => {
    setDeletingItem(item);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const header = getSampleReceiptHeaderAndLink({
    filter,
    merchant,
    merchantStore,
    device
  });

  return (
    <div>
      <div className="pb-4">
        <h1>{t('receipts.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('receipts.list.breadcrumbLabel') }]}
        />
      </div>
      <div className="w-full flex justify-between items-center">
        <h1>{header}</h1>
      </div>

      {records?.data == null ||
        (records.data.length === 0 && <SuccessMessage message={t('receipts.list.empty')} type="error" />)}

      {records?.data != null && records.data.length > 0 && (
        <>
          <DeleteItem isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />
          <ReceiptInfo isOpen={openInfoModal} toggleModal={toggleInfoModal} item={selectedItem} />
        </>
      )}

      {records?.data != null && records.data.length > 0 && (
        <>
          <ListReceipts
            items={records.data}
            toggleInfoModal={toggleInfoModal}
            toggleDeleteModal={toggleDeleteModal}
            handleInfoItem={handleSelectItem}
            handleDeletingItem={handleDeletingItem}
          />
          <Pagination pageData={records.meta} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default ListReceiptsPage;
